import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { formatTimeString, formatDateForInput, parseDateOnlyString, formatDate as formatDateUtil, parseSessionDate } from '../../utils/dateUtils';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Chip,
  Stack,
  FormControlLabel,
  Switch,
  InputAdornment,
  IconButton,
  Menu,
  Card,
  CardContent,
  CardActions,
  Autocomplete,
  FormHelperText,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  Checkbox
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  Class as ClassIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  Cancel as CancelIcon,
  FitnessCenter as FitnessCenterIcon,
  Home as HomeIcon,
  Repeat as RepeatIcon,
  Schedule as ScheduleIcon,
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as ContentCopyIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../../context/AuthContext';
import { classAPI, classTypeAPI, classLevelAPI, membershipTierAPI, tenantSettingsAPI, userAPI, classInstanceAPI } from '../../services/api';
import AdminAccessControl from '../common/AdminAccessControl';
import { Class, ClassType, ClassLevel, RecurringPattern, BookingWindowConfiguration as BookingWindowConfigurationType, MembershipTier, TenantSettings, User, ClassInstance, MemberProfile, TierAccessConfiguration, TierAccessLevel, UserRole } from '../../types';
import ClassAttendeesDialog from './ClassAttendeesDialog';
import RegisterMemberDialog from './RegisterMemberDialog';

const ClassManagementPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State for classes
  const [classes, setClasses] = useState<Class[]>([]);
  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  const [classLevels, setClassLevels] = useState<ClassLevel[]>([]);
  const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);
  const [coaches, setCoaches] = useState<User[]>([]);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // State for class instances (for recurring classes)
  const [classInstances, setClassInstances] = useState<Record<string, any[]>>({});
  const [nextInstancesInfo, setNextInstancesInfo] = useState<Record<string, { enrolledCount: number; nextInstanceDate: string }>>({});

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAttendeesDialog, setOpenAttendeesDialog] = useState(false);
  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Filter states
  const [searchText, setSearchText] = useState<string>('');
  const [coachFilter, setCoachFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('active'); // Default to active classes only
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [levelFilter, setLevelFilter] = useState<string>('');

  const DEFAULT_CLASS_DURATION = 60; // in minutes

  // Form state
  const [formData, setFormData] = useState(() => {
    const defaultStartTime = new Date();
    const defaultEndTime = new Date(defaultStartTime);
    defaultEndTime.setMinutes(defaultStartTime.getMinutes() + 60); // Add 60 minutes
    
    return {
      name: '',
      type: '',
      newType: '',
      description: '',
      coachId: '',
      instructorName: '',
      isGuestCoach: false,
      venue: '',
      level: '',
      newLevel: '',
      capacity: 0,
      scheduleDate: new Date(),
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      duration: 60, // Duration in minutes
      oneTimeCost: '',
      active: true,
      isWaitlistAvailable: true,
      recurringPattern: null as RecurringPattern | null,
      isRecurring: false,
      bookingWindows: {} as BookingWindowConfigurationType,
      tierAccess: {} as TierAccessConfiguration
    };
  });

  // Collapsible section states
  const [bookingConfigExpanded, setBookingConfigExpanded] = useState(false);
  const [tierAccessExpanded, setTierAccessExpanded] = useState(false);

  // Booking window configuration state
  const [bookingWindowCards, setBookingWindowCards] = useState<Array<{
    id: string;
    membershipTier: string;
    unlimited: boolean;
    days: number;
  }>>([]);

  // Tier access configuration state
  const [tierAccessCards, setTierAccessCards] = useState<Array<{
    id: string;
    membershipTier: string;
    accessLevel: TierAccessLevel;
  }>>([]);

  // Helper function to parse time strings that might include timezone info
  const parseTimeForEdit = (timeString: string): Date => {
    if (!timeString) return new Date();
    
    // Handle time with timezone (e.g., "20:00:00+00") or without timezone (e.g., "20:00:00")
    const timeOnly = timeString.split('+')[0].split('-')[0]; // Remove timezone if present
    const [hours, minutes, seconds] = timeOnly.split(':').map(Number);
    
    const date = new Date();
    date.setHours(hours, minutes, seconds || 0, 0);
    return date;
  };

  // Fetch data on component mount
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchClasses(),
          fetchClassTypes(),
          fetchClassLevels(),
          fetchMembershipTiers(),
          fetchTenantSettings(),
          fetchCoaches(),
        ]);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await classAPI.getClasses();
      setClasses(response.data);
      
      // Fetch instances for recurring classes
      await fetchClassInstances(response.data);
    } catch (error: any) {
      console.error('Error fetching classes:', error);
      setError('Failed to fetch classes');
    }
  };

  const fetchClassInstances = async (classData: Class[]) => {
    try {
      const recurringClasses = classData.filter(cls => cls.recurringPattern);

      // Get current date for filtering upcoming instances
      const today = new Date().toISOString().split('T')[0];
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 3);
      const endDateStr = endDate.toISOString().split('T')[0];

      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);

      // Fetch all recurring class instances in parallel
      const results = await Promise.all(
        recurringClasses.map(async (cls) => {
          try {
            const response = await classInstanceAPI.getInstances({
              parentClassId: cls.id,
              startDate: today,
              endDate: endDateStr,
              includeBookings: true
            });
            return { id: cls.id, data: response.data || [] };
          } catch (error) {
            console.error(`Error fetching instances for class ${cls.id}:`, error);
            return { id: cls.id, data: [] };
          }
        })
      );

      const instancesData: Record<string, any[]> = {};
      const nextInstanceData: Record<string, { enrolledCount: number; nextInstanceDate: string }> = {};

      for (const { id, data } of results) {
        instancesData[id] = data;

        const upcomingInstances = data
          .filter((instance: any) =>
            parseSessionDate(instance.sessionDate) >= todayMidnight &&
            instance.status !== 'CANCELLED'
          )
          .sort((a: any, b: any) => parseSessionDate(a.sessionDate).getTime() - parseSessionDate(b.sessionDate).getTime());

        if (upcomingInstances.length > 0) {
          const nextInstance = upcomingInstances[0];
          nextInstanceData[id] = {
            enrolledCount: nextInstance.enrolledCount || 0,
            nextInstanceDate: nextInstance.sessionDate
          };
        }
      }

      setClassInstances(instancesData);
      setNextInstancesInfo(nextInstanceData);
    } catch (error) {
      console.error('Error fetching class instances:', error);
    }
  };

  const fetchClassTypes = async () => {
    try {
      const response = await classTypeAPI.getAll();
      setClassTypes(response.data);
    } catch (error: any) {
      console.error('Error fetching class types:', error);
    }
  };

  const fetchClassLevels = async () => {
    try {
      const response = await classLevelAPI.getAll();
      setClassLevels(response.data);
    } catch (error: any) {
      console.error('Error fetching class levels:', error);
    }
  };

  const fetchMembershipTiers = async () => {
    try {
      const response = await membershipTierAPI.getMembershipTiers(true);
      setMembershipTiers(response.data.filter((tier: MembershipTier) => tier.active));
    } catch (error: any) {
      console.error('Error fetching membership tiers:', error);
    }
  };

  const fetchTenantSettings = async () => {
    try {
      const response = await tenantSettingsAPI.getAllSettings();
      setTenantSettings(response.data);
    } catch (error: any) {
      console.error('Error fetching tenant settings:', error);
      // Don't show error to user, just log it as this is not critical for class management
    }
  };

  const fetchCoaches = async () => {
    try {
      const response = await userAPI.getCoaches();
      setCoaches(response.data);
    } catch (error: any) {
      console.error('Error fetching coaches:', error);
    }
  };

  // Booking window card management functions
  const addBookingWindowCard = () => {
    const newCard = {
      id: `card-${Date.now()}`,
      membershipTier: '',
      unlimited: false,
      days: 7
    };
    setBookingWindowCards([...bookingWindowCards, newCard]);
  };

  const removeBookingWindowCard = (cardId: string) => {
    const updatedCards = bookingWindowCards.filter(card => card.id !== cardId);
    setBookingWindowCards(updatedCards);
    
    // Update form data - remove the booking window for this card's membership tier
    const cardToRemove = bookingWindowCards.find(card => card.id === cardId);
    if (cardToRemove && cardToRemove.membershipTier) {
      const { [cardToRemove.membershipTier]: removed, ...remainingWindows } = formData.bookingWindows;
      setFormData({
        ...formData,
        bookingWindows: remainingWindows
      });
    }
  };

  const updateBookingWindowCard = (cardId: string, field: string, value: any) => {
    const updatedCards = bookingWindowCards.map(card => {
      if (card.id === cardId) {
        const updatedCard = { ...card, [field]: value };
        
        // If membership tier changed, remove the old booking window from formData
        if (field === 'membershipTier' && card.membershipTier && card.membershipTier !== value) {
          const { [card.membershipTier]: removed, ...remainingWindows } = formData.bookingWindows;
          setFormData({
            ...formData,
            bookingWindows: remainingWindows
          });
        }
        
        // Update formData with the new booking window configuration
        if (updatedCard.membershipTier) {
          setFormData({
            ...formData,
            bookingWindows: {
              ...formData.bookingWindows,
              [updatedCard.membershipTier]: {
                advanceBookingDays: updatedCard.unlimited ? 0 : updatedCard.days,
                unlimited: updatedCard.unlimited
              }
            }
          });
        }
        
        return updatedCard;
      }
      return card;
    });
    setBookingWindowCards(updatedCards);
  };

  // Tier access card management functions
  const addTierAccessCard = () => {
    const newCard = {
      id: `tier-${Date.now()}`,
      membershipTier: '',
      accessLevel: TierAccessLevel.FREE
    };
    setTierAccessCards([...tierAccessCards, newCard]);
  };

  const removeTierAccessCard = (cardId: string) => {
    const updatedCards = tierAccessCards.filter(card => card.id !== cardId);
    setTierAccessCards(updatedCards);
    
    // Update form data - remove the tier access for this card's membership tier
    const cardToRemove = tierAccessCards.find(card => card.id === cardId);
    if (cardToRemove && cardToRemove.membershipTier) {
      const { [cardToRemove.membershipTier]: removed, ...remainingAccess } = formData.tierAccess;
      setFormData({
        ...formData,
        tierAccess: remainingAccess
      });
    }
  };

  const updateTierAccessCard = (cardId: string, field: string, value: any) => {
    const updatedCards = tierAccessCards.map(card => {
      if (card.id === cardId) {
        const updatedCard = { ...card, [field]: value };
        
        // If membership tier changed, remove the old tier access from formData
        if (field === 'membershipTier' && card.membershipTier && card.membershipTier !== value) {
          const { [card.membershipTier]: removed, ...remainingAccess } = formData.tierAccess;
          setFormData({
            ...formData,
            tierAccess: remainingAccess
          });
        }
        
        // Update formData with the new tier access configuration
        if (updatedCard.membershipTier) {
          setFormData({
            ...formData,
            tierAccess: {
              ...formData.tierAccess,
              [updatedCard.membershipTier]: updatedCard.accessLevel
            }
          });
        }
        
        return updatedCard;
      }
      return card;
    });
    setTierAccessCards(updatedCards);
  };

  const handleCreateClass = () => {
    setIsEditing(false);
    setError(null); // Clear any previous errors
    
    // Use default booking windows from tenant settings
    const defaultBookingWindows = tenantSettings?.default_booking_windows || {};
        
    // Initialize start time and calculate end time based on default duration
    const defaultStartTime = new Date();
    const defaultEndTime = new Date(defaultStartTime);
    defaultEndTime.setMinutes(defaultStartTime.getMinutes() + DEFAULT_CLASS_DURATION); // Add DEFAULT_CLASS_DURATION in minutes
    
    // Initialize tier access with all tiers set to FREE
    const initialTierAccess = membershipTiers.reduce((acc, tier) => {
      acc[tier.name] = TierAccessLevel.FREE;
      return acc;
    }, {} as TierAccessConfiguration);
    
    setFormData({
      name: '',
      type: '',
      newType: '',
      description: '',
      coachId: '',
      instructorName: '',
      isGuestCoach: false,
      venue: '',
      level: '',
      newLevel: '',
      capacity: 0,
      scheduleDate: new Date(),
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      duration: DEFAULT_CLASS_DURATION, // Duration in minutes
      oneTimeCost: '',
      active: true,
      isWaitlistAvailable: true,
      recurringPattern: null,
      isRecurring: false,
      bookingWindows: defaultBookingWindows,
      tierAccess: initialTierAccess
    });
    
    // Initialize booking window cards from default settings
    const windowCards = Object.entries(defaultBookingWindows).map(([tierName, window], index) => ({
      id: `card-${Date.now()}-${index}`,
      membershipTier: tierName,
      unlimited: window.unlimited || false,
      days: window.advanceBookingDays || 7
    }));
    setBookingWindowCards(windowCards);
    
    // Initialize tier access cards (default to free for all tiers)
    const accessCards = membershipTiers.map((tier, index) => ({
      id: `tier-${Date.now()}-${index}`,
      membershipTier: tier.name,
      accessLevel: TierAccessLevel.FREE
    }));
    setTierAccessCards(accessCards);
    
    setBookingConfigExpanded(false);
    setTierAccessExpanded(false);
    setOpenCreateDialog(true);
  };

  // Helper function to calculate duration in minutes
  const calculateDuration = (startTime: Date, endTime: Date): number => {
    const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
    const endMinutes = endTime.getHours() * 60 + endTime.getMinutes();
    let duration = endMinutes - startMinutes;
    
    // Handle case where end time is next day (e.g., 11:30 PM to 1:00 AM)
    if (duration < 0) {
      duration += 24 * 60; // Add 24 hours in minutes
    }
    
    return Math.max(duration, 0); // Ensure non-negative duration
  };

  const handleEditClass = (classItem: Class) => {
    // Navigate to dedicated edit page
    navigate(`/classes/edit/${classItem.id}`);
  };

  const handleDeleteClass = (classItem: Class) => {
    setSelectedClass(classItem);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteClass = async () => {
    if (!selectedClass) return;

    try {
      await classAPI.deleteClass(selectedClass.id);
      setClasses(classes.filter(c => c.id !== selectedClass.id));
      setOpenDeleteDialog(false);
      setSelectedClass(null);
    } catch (error: any) {
      console.error('Error deleting class:', error);
      setError('Failed to delete class');
    }
  };

  const handleViewAttendees = (classItem: Class) => {
    setSelectedClass(classItem);
    setOpenAttendeesDialog(true);
  };

  const handleRegisterMember = (classItem: Class) => {
    setSelectedClass(classItem);
    setOpenRegisterDialog(true);
  };

  const handleCopyClass = (classItem: Class) => {
    setIsEditing(false);
    setError(null);
    
    // Parse times from the original class
    const startTime = parseTimeForEdit(classItem.startTime);
    const endTime = parseTimeForEdit(classItem.endTime);
    const duration = calculateDuration(startTime, endTime);
    const scheduleDateParsed = parseDateOnlyString(classItem.scheduleDate);
    
    // Copy all class data to form
    setFormData({
      name: `${classItem.name} - Copy`,
      type: classItem.type,
      newType: '',
      description: classItem.description || '',
      coachId: classItem.coachId || '',
      instructorName: classItem.instructorName || classItem.instructor || '',
      isGuestCoach: classItem.isGuestCoach || !classItem.coachId,
      venue: classItem.venue,
      level: classItem.level,
      newLevel: '',
      capacity: classItem.capacity,
      scheduleDate: scheduleDateParsed,
      startTime,
      endTime,
      duration,
      oneTimeCost: classItem.oneTimeCost?.toString() || '',
      active: classItem.active,
      isWaitlistAvailable: classItem.isWaitlistAvailable ?? true,
      recurringPattern: (() => {
        if (!classItem.recurringPattern) return null;
        return {
          frequency: classItem.recurringPattern.frequency || 'weekly',
          interval: classItem.recurringPattern.interval || 1,
          daysOfWeek: classItem.recurringPattern.daysOfWeek || [],
          endDate: classItem.recurringPattern.endDate || undefined
        };
      })(),
      isRecurring: !!classItem.recurringPattern,
      bookingWindows: classItem.bookingWindows || {},
      tierAccess: classItem.tierAccess || {}
    });
    
    // Initialize booking window cards from copied data
    const existingWindows = classItem.bookingWindows || {};
    const windowCards = Object.entries(existingWindows).map(([tierName, windowConfig], index) => ({
      id: `card-${Date.now()}-${index}`,
      membershipTier: tierName,
      unlimited: (windowConfig as any).unlimited || false,
      days: (windowConfig as any).advanceBookingDays || 7
    }));
    setBookingWindowCards(windowCards);
    setBookingConfigExpanded(Object.keys(existingWindows).length > 0);
    
    // Initialize tier access cards from copied data
    const existingAccess = classItem.tierAccess || {};
    const accessCards = Object.entries(existingAccess).map(([tierName, accessLevel], index) => ({
      id: `tier-${Date.now()}-${index}`,
      membershipTier: tierName,
      accessLevel: accessLevel as TierAccessLevel
    }));
    setTierAccessCards(accessCards);
    setTierAccessExpanded(Object.keys(existingAccess).length > 0);
    
    setOpenCreateDialog(true);
  };

  const handleRegistrationSuccess = (message?: string) => {
    // Show success message (only if provided, to avoid duplicate snackbars)
    if (message) {
      setSuccessMessage(message);
    }
    
    // Refresh classes and class instances to update booking counts
    setTimeout(() => {
      fetchClasses(); // This will also refresh class instances for recurring classes
    }, 500);
  };

  // Helper function to handle duration change
  const handleDurationChange = (durationMinutes: number) => {
    const startTime = formData.startTime;
    const newEndTime = new Date(startTime);
    newEndTime.setMinutes(startTime.getMinutes() + durationMinutes);
    
    setFormData({
      ...formData,
      duration: durationMinutes,
      endTime: newEndTime
    });
  };

  // Helper function to handle start time change
  const handleStartTimeChange = (newStartTime: Date) => {
    const newEndTime = new Date(newStartTime);
    newEndTime.setMinutes(newStartTime.getMinutes() + formData.duration);
    
    setFormData({
      ...formData,
      startTime: newStartTime,
      endTime: newEndTime
    });
  };

  // Helper function to handle end time change
  const handleEndTimeChange = (newEndTime: Date) => {
    const duration = calculateDuration(formData.startTime, newEndTime);
    
    // If duration is negative, don't allow it
    if (formData.startTime >= newEndTime) {
      setError('End time must be after start time');
      return;
    }
    
    setError(null); // Clear error if times are valid
    setFormData({
      ...formData,
      endTime: newEndTime,
      duration: duration
    });
  };

  const handleClassDayOfWeekChange = (day: number, checked: boolean) => {
    const currentDays = formData.recurringPattern?.daysOfWeek || [];
    let newDays;
    
    // Get the day of week for the schedule date (0 = Sunday, 1 = Monday, etc.)
    const scheduleDateDayOfWeek = formData.scheduleDate.getDay();
    
    if (checked) {
      newDays = [...currentDays, day].sort();
    } else {
      // Prevent unchecking the schedule date's day of week
      if (day === scheduleDateDayOfWeek) {
        // Show a helpful message to the user
        setError(`Cannot unselect ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][day]} as it matches the class schedule date (${formatDateUtil(formData.scheduleDate.toISOString())}). To change this, update the schedule date first.`);
        return; // Don't update the state
      }
      newDays = currentDays.filter(d => d !== day);
    }
    
    setFormData({
      ...formData,
      recurringPattern: {
        ...formData.recurringPattern!,
        daysOfWeek: newDays
      }
    });
  };

  const handleSubmit = async () => {
    // Clear any previous errors
    setError(null);

    // Validate time logic
    if (formData.startTime >= formData.endTime) {
      setError('End time must be after start time');
      return;
    }

    // Validate minimum duration (e.g., 15 minutes)
    const duration = calculateDuration(formData.startTime, formData.endTime);
    if (duration < 15) {
      setError('Class duration must be at least 15 minutes');
      return;
    }
    setError(null);
    
    try {
      // Create new class type if needed
      let finalType = formData.type;
      if (formData.newType.trim()) {
        const typeResponse = await classTypeAPI.create({
          name: formData.newType.trim()
        });
        finalType = formData.newType.trim();
        await fetchClassTypes();
      }

      // Create new class level if needed
      let finalLevel = formData.level;
      if (formData.newLevel.trim()) {
        const levelResponse = await classLevelAPI.create({
          name: formData.newLevel.trim()
        });
        finalLevel = formData.newLevel.trim();
        await fetchClassLevels();
      }

      // Format times
      const startTime = formData.startTime.toTimeString().split(' ')[0].slice(0, 5);
      const endTime = formData.endTime.toTimeString().split(' ')[0].slice(0, 5);

      // Create recurring pattern if needed
      let recurringPattern = null;
      if (formData.isRecurring && formData.recurringPattern) {
        recurringPattern = formData.recurringPattern;
      }

      const classData = {
        name: formData.name,
        type: finalType,
        description: formData.description,
        coachId: formData.isGuestCoach ? null : formData.coachId,
        instructorName: formData.instructorName,
        isGuestCoach: formData.isGuestCoach,
        venue: formData.venue,
        level: finalLevel,
        capacity: formData.capacity,
        scheduleDate: formatDateForInput(formData.scheduleDate),
        startTime,
        endTime,
        oneTimeCost: formData.oneTimeCost ? parseFloat(formData.oneTimeCost) : null,
        active: formData.active,
        isWaitlistAvailable: formData.isWaitlistAvailable,
        recurringPattern,
        bookingWindows: formData.bookingWindows,
        tierAccess: formData.tierAccess
      };

      if (isEditing && selectedClass) {
        await classAPI.updateClass(selectedClass.id, classData);
      } else {
        await classAPI.createClass(classData);
      }

      await fetchClasses();
      setOpenCreateDialog(false);
    } catch (error: any) {
      console.error('Error saving class:', error);
      setError(isEditing ? 'Failed to update class' : 'Failed to create class');
    }
  };

  const formatTime = (timeString: string) => {
    return formatTimeString(timeString);
  };

  const formatDate = (dateString: string) => {
    return formatDateUtil(dateString);
  };

  // Filter classes based on search and filter criteria
  const filteredClasses = useMemo(() => {
    let filtered = [...classes];

    // Text search (class name, type, instructor)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(classItem =>
        classItem.name.toLowerCase().includes(searchLower) ||
        classItem.type.toLowerCase().includes(searchLower) ||
        (classItem.instructorName && classItem.instructorName.toLowerCase().includes(searchLower)) ||
        (classItem.instructor && classItem.instructor.toLowerCase().includes(searchLower))
      );
    }

    // Coach filter
    if (coachFilter) {
      if (coachFilter === 'guest') {
        // Show guest coaches (those without a coachId or where isGuestCoach is true)
        filtered = filtered.filter(classItem => !classItem.coachId || classItem.isGuestCoach);
      } else {
        // Show specific coach
        filtered = filtered.filter(classItem => classItem.coachId === coachFilter);
      }
    }

    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(classItem => classItem.active === true);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(classItem => classItem.active === false);
    }
    // If statusFilter is empty string, show all

    // Type filter
    if (typeFilter) {
      filtered = filtered.filter(classItem => classItem.type === typeFilter);
    }

    // Level filter
    if (levelFilter) {
      filtered = filtered.filter(classItem => classItem.level === levelFilter);
    }

    return filtered;
  }, [classes, searchText, coachFilter, statusFilter, typeFilter, levelFilter]);

  // Clear all filters
  const clearFilters = () => {
    setSearchText('');
    setCoachFilter('');
    setStatusFilter('active'); // Reset to default
    setTypeFilter('');
    setLevelFilter('');
  };

  // Get unique types from classes
  const getUniqueTypes = () => {
    const types = classes.map(classItem => classItem.type);
    return [...new Set(types)].sort();
  };

  // Get unique levels from classes
  const getUniqueLevels = () => {
    const levels = classes.map(classItem => classItem.level);
    return [...new Set(levels)].sort();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg">
        <AdminAccessControl allowedRoles={[UserRole.ADMIN, UserRole.COACH, UserRole.EMPLOYEE, UserRole.SUPER_ADMIN]}>
        <Box sx={{ mt: 4, mb: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Button
              component={RouterLink}
              to="/dashboard"
              color="inherit"
              startIcon={<HomeIcon />}
              sx={{ textTransform: 'none' }}
            >
              Dashboard
            </Button>
            <Typography color="text.primary">Class Management</Typography>
          </Breadcrumbs>

          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              <FitnessCenterIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              Class Management
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchClasses}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateClass}
                size="large"
              >
                Create Class
              </Button>
            </Stack>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Success Snackbar */}
          <Snackbar
            open={!!successMessage}
            autoHideDuration={4000}
            onClose={() => setSuccessMessage(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setSuccessMessage(null)} severity="success">
              {successMessage}
            </Alert>
          </Snackbar>

          {/* Filters */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  placeholder="Search by class name, type, or instructor..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="small"
                  sx={{ minWidth: 300 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Coach</InputLabel>
                  <Select
                    value={coachFilter}
                    onChange={(e: SelectChangeEvent) => setCoachFilter(e.target.value)}
                    label="Coach"
                  >
                    <MenuItem value="">All Coaches</MenuItem>
                    <MenuItem value="guest">Guest Coaches</MenuItem>
                    {coaches.map((coach) => (
                      <MenuItem key={coach.id} value={coach.id}>
                        {coach.firstName} {coach.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={(e: SelectChangeEvent) => setTypeFilter(e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {getUniqueTypes().map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Level</InputLabel>
                  <Select
                    value={levelFilter}
                    onChange={(e: SelectChangeEvent) => setLevelFilter(e.target.value)}
                    label="Level"
                  >
                    <MenuItem value="">All Levels</MenuItem>
                    {getUniqueLevels().map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  startIcon={<ClearIcon />}
                  size="small"
                >
                  Clear Filters
                </Button>
              </Box>
            </Stack>
          </Paper>

          {/* Classes Grid */}
          <Grid container spacing={3}>
            {filteredClasses.map((classItem) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={classItem.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography 
                        variant="h6" 
                        component="h2" 
                        noWrap
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { color: 'primary.main', textDecoration: 'underline' }
                        }}
                        onClick={() => navigate(`/classes/${classItem.id}`, { state: { from: 'class-management' } })}
                        title="Click to view class details"
                      >
                        {classItem.name}
                      </Typography>
                      <Chip
                        label={classItem.active ? 'Active' : 'Inactive'}
                        color={classItem.active ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ClassIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {classItem.type} - {classItem.level}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOnIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {classItem.venue}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ScheduleIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(classItem.scheduleDate)} - {formatTime(classItem.startTime)} to {formatTime(classItem.endTime)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleViewAttendees(classItem)}>
                        <PeopleIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="primary.main" sx={{ textDecoration: 'underline' }}>
                          {classItem.recurringPattern && nextInstancesInfo[classItem.id] ? (
                            <>
                              {nextInstancesInfo[classItem.id].enrolledCount} / {classItem.capacity} enrolled
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                ({formatDateUtil(nextInstancesInfo[classItem.id].nextInstanceDate)})
                              </Typography>
                            </>
                          ) : (
                            `${classItem.currentBookings || 0} / ${classItem.capacity} enrolled`
                          )}
                        </Typography>
                      </Box>
                      
                      {classItem.oneTimeCost && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AttachMoneyIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            ${classItem.oneTimeCost}
                          </Typography>
                        </Box>
                      )}
                      
                      {classItem.recurringPattern && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <RepeatIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Recurring {classItem.recurringPattern.frequency}
                          </Typography>
                        </Box>
                      )}
                      
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                        Instructor: {classItem.instructorName || classItem.instructor}
                      </Typography>
                      
                      {classItem.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {classItem.description}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                  
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Button
                      size="small"
                      onClick={() => handleCopyClass(classItem)}
                      title="Copy class to create a new one"
                    >
                      <ContentCopyIcon />
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PersonAddIcon />}
                      onClick={() => handleRegisterMember(classItem)}
                    >
                      Enroll
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {classes.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <FitnessCenterIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No classes found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create your first class to get started
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateClass}>
                Create Class
              </Button>
            </Paper>
          ) : filteredClasses.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <FitnessCenterIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No classes match the current filters
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Try adjusting your search criteria or clear all filters
              </Typography>
              <Button variant="outlined" startIcon={<ClearIcon />} onClick={clearFilters}>
                Clear Filters
              </Button>
            </Paper>
          )}
        </Box>

        {/* Create/Edit Class Dialog */}
        <Dialog
          open={openCreateDialog}
          disableEscapeKeyDown={true}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {isEditing ? 'Edit Class' : 'Create New Class'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Class Name */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Class Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>

              {/* Coach Selection */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <Autocomplete
                    options={coaches}
                    getOptionLabel={(coach) => coach.firstName && coach.lastName ? `${coach.firstName} ${coach.lastName}` : coach.email || 'Unknown Coach'}
                    value={coaches.find(coach => coach.id === formData.coachId) || null}
                    onChange={(event, newValue) => {
                      setFormData({ 
                        ...formData, 
                        coachId: newValue?.id || '',
                        instructorName: newValue ? `${newValue.firstName} ${newValue.lastName}` : formData.instructorName
                      });
                    }}
                    disabled={formData.isGuestCoach}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Coach"
                        required={!formData.isGuestCoach}
                      />
                    )}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isGuestCoach}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          isGuestCoach: e.target.checked,
                          coachId: e.target.checked ? '' : formData.coachId
                        })}
                      />
                    }
                    label="Guest Coach/Instructor"
                  />
                  {formData.isGuestCoach && (
                    <TextField
                      label="Guest Coach Name"
                      value={formData.instructorName}
                      onChange={(e) => setFormData({ ...formData, instructorName: e.target.value })}
                      fullWidth
                      required
                      placeholder="Enter coach/instructor name"
                    />
                  )}
                </Stack>
              </Grid>

              {/* Class Type */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  freeSolo
                  options={classTypes.map((type) => type.name)}
                  value={formData.type}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, type: newValue || '', newType: '' });
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFormData({ ...formData, type: newInputValue, newType: newInputValue });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Class Type"
                      helperText="Select existing or type new"
                      required
                    />
                  )}
                />
              </Grid>

              {/* Class Level */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  freeSolo
                  options={classLevels.map((level) => level.name)}
                  value={formData.level}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, level: newValue || '', newLevel: '' });
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFormData({ ...formData, level: newInputValue, newLevel: newInputValue });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Class Level"
                      helperText="Select existing or type new"
                      required
                    />
                  )}
                />
              </Grid>

              {/* Venue */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Venue"
                  value={formData.venue}
                  onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>

              {/* Capacity */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })}
                  fullWidth
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>

              {/* Schedule Date */}
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Date"
                  value={formData.scheduleDate}
                  onChange={(date) => {
                    const newDate = date || new Date();
                    
                    // If we have weekly recurrence enabled, auto-include the new date's day
                    if (formData.isRecurring && formData.recurringPattern?.frequency === 'weekly') {
                      const newDayOfWeek = newDate.getDay();
                      const currentDays = formData.recurringPattern.daysOfWeek || [];
                      
                      // Add the new day if it's not already included
                      if (!currentDays.includes(newDayOfWeek)) {
                        const updatedDays = [...currentDays, newDayOfWeek].sort();
                        setFormData({ 
                          ...formData, 
                          scheduleDate: newDate,
                          recurringPattern: {
                            ...formData.recurringPattern,
                            daysOfWeek: updatedDays
                          }
                        });
                        return;
                      }
                    }
                    
                    setFormData({ ...formData, scheduleDate: newDate });
                  }}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>

              {/* Duration */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Duration (minutes)"
                  type="number"
                  value={formData.duration}
                  onChange={(e) => handleDurationChange(parseInt(e.target.value) || 60)}
                  fullWidth
                  required
                  inputProps={{ min: 15, max: 480 }}
                  helperText="Duration in minutes (15-480)"
                />
              </Grid>

              {/* Start Time */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TimePicker
                  label="Start Time"
                  value={formData.startTime}
                  onChange={(time) => handleStartTimeChange(time || new Date())}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>

              {/* End Time */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TimePicker
                  label="End Time"
                  value={formData.endTime}
                  onChange={(time) => handleEndTimeChange(time || new Date())}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>

              {/* One Time Cost */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="One-time Cost"
                  type="number"
                  value={formData.oneTimeCost}
                  onChange={(e) => setFormData({ ...formData, oneTimeCost: e.target.value })}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              {/* Active Status */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />
                  }
                  label="Active"
                  sx={{ mt: 2 }}
                />
              </Grid>

              {/* Waitlist Available */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isWaitlistAvailable}
                      onChange={async (e) => {
                        // If trying to disable waitlist, check if there are waitlisted users
                        if (!e.target.checked && selectedClass) {
                          try {
                            // Fetch current attendees to check waitlist
                            const attendeesResponse = await classAPI.getClassBookings(selectedClass.id);
                            const waitlistCount = attendeesResponse.data.waitlistCount || 0;
                            
                            if (waitlistCount > 0) {
                              setError(`Cannot disable waitlist - there are ${waitlistCount} user(s) currently on the waitlist. Please handle the waitlisted users first.`);
                              return;
                            }
                          } catch (error) {
                            console.error('Error checking waitlist:', error);
                            // Continue anyway - backend validation will catch it
                          }
                        }
                        setError(null);
                        setFormData({ ...formData, isWaitlistAvailable: e.target.checked });
                      }}
                    />
                  }
                  label="Allow Waitlist"
                  sx={{ mt: 2 }}
                />
              </Grid>

              {/* Description */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>

              {/* Recurring Pattern */}
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isRecurring}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        isRecurring: e.target.checked,
                        recurringPattern: e.target.checked ? {
                          frequency: 'weekly',
                          interval: 1,
                          daysOfWeek: [formData.scheduleDate.getDay()]
                        } : null
                      })}
                    />
                  }
                  label="Recurring Class"
                />
              </Grid>

              {formData.isRecurring && (
                <>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Frequency</InputLabel>
                      <Select
                        value={formData.recurringPattern?.frequency || 'weekly'}
                        onChange={(e) => {
                          const newFrequency = e.target.value as 'daily' | 'weekly' | 'monthly';
                          let updatedPattern = {
                            ...formData.recurringPattern!,
                            frequency: newFrequency
                          };
                          
                          // If switching to weekly, ensure schedule date's day is included
                          if (newFrequency === 'weekly') {
                            const scheduleDateDayOfWeek = formData.scheduleDate.getDay();
                            const currentDays = formData.recurringPattern?.daysOfWeek || [];
                            
                            if (!currentDays.includes(scheduleDateDayOfWeek)) {
                              updatedPattern.daysOfWeek = [...currentDays, scheduleDateDayOfWeek].sort();
                            }
                          }
                          
                          setFormData({
                            ...formData,
                            recurringPattern: updatedPattern
                          });
                        }}
                      >
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="Interval"
                      type="number"
                      value={formData.recurringPattern?.interval || 1}
                      onChange={(e) => setFormData({
                        ...formData,
                        recurringPattern: {
                          ...formData.recurringPattern!,
                          interval: parseInt(e.target.value) || 1
                        }
                      })}
                      fullWidth
                      inputProps={{ min: 1, max: 12 }}
                      helperText={`Every ${formData.recurringPattern?.interval || 1} ${(formData.recurringPattern?.frequency || 'weekly').slice(0, -2) === 'dai' ? 'day' : (formData.recurringPattern?.frequency || 'weekly').slice(0, -2)}${(formData.recurringPattern?.interval || 1) > 1 ? 's' : ''}`}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <DatePicker
                      label="End Date (Optional)"
                      value={formData.recurringPattern?.endDate ? parseDateOnlyString(formData.recurringPattern.endDate) : null}
                      onChange={(date) => setFormData({
                        ...formData,
                        recurringPattern: {
                          ...formData.recurringPattern!,
                          endDate: date ? formatDateForInput(date) : undefined
                        }
                      })}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>

                  {/* Days of Week (for weekly) */}
                  {formData.recurringPattern?.frequency === 'weekly' && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Days of Week
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        The class date's day ({['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][formData.scheduleDate.getDay()]}) is automatically included and cannot be removed.
                      </Typography>
                      <FormGroup row>
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => {
                          const isClassDateDay = index === formData.scheduleDate.getDay();
                          return (
                            <FormControlLabel
                              key={index}
                              control={
                                <Checkbox
                                  checked={formData.recurringPattern?.daysOfWeek?.includes(index) || false}
                                  onChange={(e) => handleClassDayOfWeekChange(index, e.target.checked)}
                                  disabled={isClassDateDay} // Disable the class date's day
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {day}
                                  {isClassDateDay && (
                                    <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                                      (Class Date)
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          );
                        })}
                      </FormGroup>
                    </Grid>
                  )}
                </>
              )}
            </Grid>

            {/* Collapsible Booking Window Configuration */}
            <Accordion expanded={bookingConfigExpanded} onChange={() => setBookingConfigExpanded(!bookingConfigExpanded)} sx={{ mt: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Booking Window Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={3}>
                  <Typography variant="body2" color="text.secondary">
                    Configure booking windows for different membership tiers. Members can book this class up to the specified number of days in advance.
                  </Typography>

                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Booking Windows
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addBookingWindowCard}
                      >
                        Add Window
                      </Button>
                    </Box>
                    
                    {bookingWindowCards.length === 0 ? (
                      <Alert severity="info">
                        No booking windows configured. Add booking windows to control how far in advance different membership tiers can book this class.
                      </Alert>
                    ) : (
                      <Stack spacing={2}>
                        {bookingWindowCards.map((card) => (
                          <Card key={card.id} variant="outlined">
                            <CardContent sx={{ pb: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2">Booking Window</Typography>
                                <IconButton 
                                  color="error" 
                                  size="small" 
                                  onClick={() => removeBookingWindowCard(card.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                              
                              <Stack spacing={2}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                  <FormControl sx={{ flex: 1 }} size="small">
                                    <InputLabel>Membership Tier</InputLabel>
                                    <Select
                                      value={card.membershipTier}
                                      label="Membership Tier"
                                      onChange={(e) => updateBookingWindowCard(card.id, 'membershipTier', e.target.value)}
                                    >
                                      {membershipTiers
                                        .filter(tier => !bookingWindowCards.some(otherCard => 
                                          otherCard.id !== card.id && otherCard.membershipTier === tier.name
                                        ))
                                        .map((tier) => (
                                          <MenuItem key={tier.id} value={tier.name}>
                                            {tier.name}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </FormControl>
                                  
                                  {!card.unlimited && (
                                    <TextField
                                      label="Advance Booking Days"
                                      type="number"
                                      value={card.days}
                                      onChange={(e) => {
                                        const value = parseInt(e.target.value, 10);
                                        if (!isNaN(value) && value > 0) {
                                          updateBookingWindowCard(card.id, 'days', value);
                                        }
                                      }}
                                      size="small"
                                      sx={{ width: 180 }}
                                      InputProps={{
                                        inputProps: { min: 1, max: 365 }
                                      }}
                                    />
                                  )}
                                </Box>
                                
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={card.unlimited}
                                      onChange={(e) => updateBookingWindowCard(card.id, 'unlimited', e.target.checked)}
                                    />
                                  }
                                  label="Unlimited advance booking"
                                />
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Collapsible Tier Access Configuration */}
            <Accordion expanded={tierAccessExpanded} onChange={() => setTierAccessExpanded(!tierAccessExpanded)} sx={{ mt: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Tier Access Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={3}>
                  <Typography variant="body2" color="text.secondary">
                    Configure class access for different membership tiers. Set whether members can book for free, must pay, or have no access.
                  </Typography>

                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Tier Access
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addTierAccessCard}
                      >
                        Add Tier
                      </Button>
                    </Box>
                    
                    {tierAccessCards.length === 0 ? (
                      <Alert severity="info">
                        No tier access configured. By default, all tiers have free access to this class.
                      </Alert>
                    ) : (
                      <Stack spacing={2}>
                        {tierAccessCards.map((card) => (
                          <Card key={card.id} variant="outlined">
                            <CardContent sx={{ pb: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2">Tier Access Rule</Typography>
                                <IconButton 
                                  color="error" 
                                  size="small" 
                                  onClick={() => removeTierAccessCard(card.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                              
                              <Stack spacing={2}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                  <FormControl sx={{ flex: 1 }} size="small">
                                    <InputLabel>Membership Tier</InputLabel>
                                    <Select
                                      value={card.membershipTier}
                                      label="Membership Tier"
                                      onChange={(e) => updateTierAccessCard(card.id, 'membershipTier', e.target.value)}
                                    >
                                      {membershipTiers
                                        .filter(tier => !tierAccessCards.some(otherCard => 
                                          otherCard.id !== card.id && otherCard.membershipTier === tier.name
                                        ))
                                        .map((tier) => (
                                          <MenuItem key={tier.id} value={tier.name}>
                                            {tier.name}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </FormControl>
                                  
                                  <FormControl sx={{ flex: 1 }} size="small">
                                    <InputLabel>Access Level</InputLabel>
                                    <Select
                                      value={card.accessLevel}
                                      label="Access Level"
                                      onChange={(e) => updateTierAccessCard(card.id, 'accessLevel', e.target.value)}
                                    >
                                      <MenuItem value={TierAccessLevel.FREE}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Chip label="Free" color="success" size="small" />
                                          <Typography variant="body2">Included in membership</Typography>
                                        </Box>
                                      </MenuItem>
                                      <MenuItem value={TierAccessLevel.PAID}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Chip label="Paid" color="warning" size="small" />
                                          <Typography variant="body2">Pay per class</Typography>
                                        </Box>
                                      </MenuItem>
                                      <MenuItem value={TierAccessLevel.NO_ACCESS}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Chip label="No Access" color="error" size="small" />
                                          <Typography variant="body2">Not accessible</Typography>
                                        </Box>
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </Box>
                                
                                {card.accessLevel === TierAccessLevel.PAID && (
                                  <Alert severity="info" sx={{ mt: 1 }}>
                                    Members with this tier will be charged the "One-Time Cost" + 13% tax when booking.
                                  </Alert>
                                )}
                                
                                {card.accessLevel === TierAccessLevel.NO_ACCESS && (
                                  <Alert severity="warning" sx={{ mt: 1 }}>
                                    Members with this tier will not see this class or be able to book it.
                                  </Alert>
                                )}
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', gap: 2, alignItems: 'stretch' }}>
            {/* Error Alert inside Dialog */}
            {error && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
              </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Box>
                {isEditing && (
                  <Button 
                    onClick={() => {
                      setOpenCreateDialog(false);
                      setOpenDeleteDialog(true);
                    }}
                    color="error" 
                    startIcon={<DeleteIcon />}
                  >
                    Delete
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={() => {
                  setOpenCreateDialog(false);
                  setError(null); // Clear error when closing dialog
                }}>Cancel</Button>
                <Button 
                  onClick={handleSubmit} 
                  variant="contained"
                  disabled={!formData.name || !formData.type || !formData.level || (!formData.isGuestCoach && !formData.coachId) || (formData.isGuestCoach && !formData.instructorName) || !formData.venue || !formData.capacity || formData.capacity <= 0}
                >
                  {isEditing ? 'Update' : 'Create'} Class
                </Button>
              </Box>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          disableEscapeKeyDown={true}
        >
          <DialogTitle>Delete Class</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete "{selectedClass?.name}"? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button onClick={confirmDeleteClass} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Class Attendees Dialog */}
        <ClassAttendeesDialog
          open={openAttendeesDialog}
          onClose={() => setOpenAttendeesDialog(false)}
          classItem={selectedClass}
          onSuccess={handleRegistrationSuccess}
        />

        {/* Register Member Dialog */}
        <RegisterMemberDialog
          open={openRegisterDialog}
          onClose={() => setOpenRegisterDialog(false)}
          classItem={selectedClass}
          onSuccess={handleRegistrationSuccess}
        />
        </AdminAccessControl>
      </Container>
    </LocalizationProvider>
  );
};

export default ClassManagementPage;
